import type { SchedulerFlight } from '../hooks/useSchedulerFlights'

/**
 * Which flight should be selected for a given list.
 *
 * The `?? flights[0]` fallback is load-bearing: the backend's
 * `ensureDefaultFlight` only CREATES a flight when a schedule has none — it
 * never PROMOTES one. Deleting the default therefore leaves every survivor with
 * `is_default: false`, and a default-only rule would select nothing.
 */
export const pickFlightToSelect = (
  flights: SchedulerFlight[]
): SchedulerFlight | null =>
  flights.find((flight) => flight.is_default) ?? flights[0] ?? null

/**
 * Whether the current selection no longer refers to a flight that exists.
 *
 * A membership check, not a null check: `ensureDefaultFlight` runs on the list
 * endpoint, so after a refetch the list is never empty and "no id yet" alone
 * would leave a deleted flight's id selected forever.
 */
export const isFlightSelectionStale = (
  flights: SchedulerFlight[],
  currentFlightId: string | null
): boolean => {
  if (flights.length === 0) {
    return false
  }
  if (!currentFlightId) {
    return true
  }
  return !flights.some((flight) => flight.id === currentFlightId)
}
